import { useEffect, useState } from 'react';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { Textarea } from '~/components/ui/textarea';
import { useAuthStore } from '~/store/auth.store';

import {
  useBatchNeedsAttention,
  useBatchRuns,
  useExcludeRemaining,
} from '../../hooks/use-batch-retry';
import type { BatchExclusionReason, NeedsAttention } from '../../types/batch-retry.api.types';

export interface ExcludeRemainingDialogProps {
  jobId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Max length of the optional note (contract: at most 500 characters). */
export const EXCLUDE_NOTE_MAX_LENGTH = 500;

const REASON_OPTIONS: readonly { value: BatchExclusionReason; label: string }[] = [
  { value: 'PROVIDER_FAILING', label: 'AI provider keeps failing on these products' },
  { value: 'DATA_NEEDS_FIX', label: 'Product data needs fixing first' },
  { value: 'NOT_NEEDED', label: 'Not needed in this batch' },
];

interface ReasonBar {
  key: string;
  label: string;
  count: number;
}

/** "Why they were not enriched": failure reasons plus the never-attempted bucket. */
function toReasonBars(needsAttention: NeedsAttention | undefined): ReasonBar[] {
  const bars: ReasonBar[] = [];
  for (const bucket of needsAttention?.buckets ?? []) {
    if (bucket.code === 'FLAGGED_FOR_REVIEW') continue;
    if (bucket.reasons.length > 0) {
      for (const r of bucket.reasons) {
        if (r.count > 0)
          bars.push({ key: `${bucket.code}:${r.reason}`, label: r.label, count: r.count });
      }
    } else if (bucket.count > 0) {
      bars.push({ key: bucket.code, label: bucket.label, count: bucket.count });
    }
  }
  return bars;
}

function ReasonBars({ bars }: { bars: ReasonBar[] }) {
  if (bars.length === 0) {
    return <span className="text-muted-foreground text-xs">Nothing left in this view.</span>;
  }
  const max = Math.max(1, ...bars.map((b) => b.count));
  return (
    <div data-testid="exclude-remaining-reason-bars" className="space-y-1.5">
      {bars.map((b) => (
        <div key={b.key} className="grid grid-cols-[1fr_2fr_auto] items-center gap-3 text-xs">
          <span className="truncate">{b.label}</span>
          <div className="h-1.5 overflow-hidden rounded-full bg-neutral-200">
            <div
              className="bg-warning h-full rounded-full"
              style={{ width: `${String(Math.max(2, (b.count / max) * 100))}%` }}
            />
          </div>
          <span className="font-semibold tabular-nums">{b.count.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

export function ExcludeRemainingDialog({ jobId, open, onOpenChange }: ExcludeRemainingDialogProps) {
  // Mirrors the form's mutation so outside-click / Esc cannot dismiss mid-request.
  const [pending, setPending] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Radix unmounts the content when closed, so the form (and its idempotency key) is fresh per open. */}
      <DialogContent
        data-testid="exclude-remaining-dialog"
        className="max-w-lg"
        onInteractOutside={(e) => {
          if (pending) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (pending) e.preventDefault();
        }}
      >
        <ExcludeRemainingForm
          jobId={jobId}
          onPendingChange={setPending}
          onClose={() => {
            onOpenChange(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

function ExcludeRemainingForm({
  jobId,
  onClose,
  onPendingChange,
}: {
  jobId: string;
  onClose: () => void;
  onPendingChange: (pending: boolean) => void;
}) {
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const requestedBy = useAuthStore((s) => s.user?.email);
  const [reason, setReason] = useState<BatchExclusionReason | null>(null);
  const [note, setNote] = useState('');
  const { data: runs } = useBatchRuns(jobId);
  const { data: needsAttention } = useBatchNeedsAttention(jobId);
  const { mutate, isPending } = useExcludeRemaining(jobId);
  useEffect(() => {
    onPendingChange(isPending);
    return () => {
      onPendingChange(false);
    };
  }, [isPending, onPendingChange]);

  // Frozen on submit: the success refetch zeroes the live counts while the dialog fades out.
  const [submitted, setSubmitted] = useState<{ remaining: number; bars: ReasonBar[] } | null>(null);
  const counts = runs?.counts;
  const liveRemaining = counts
    ? counts.failedRetryable + counts.failedPermanent + (counts.notAttempted ?? 0)
    : 0;
  const remaining = submitted?.remaining ?? liveRemaining;
  const bars = submitted?.bars ?? toReasonBars(needsAttention);
  const plural = remaining === 1 ? '' : 's';

  function handleExclude() {
    if (!reason) return;
    const trimmed = note.trim();
    setSubmitted({ remaining, bars });
    mutate(
      {
        reason,
        idempotencyKey,
        ...(trimmed !== '' ? { note: trimmed } : {}),
        ...(requestedBy ? { requestedBy } : {}),
      },
      {
        onSuccess: onClose,
        onError: () => {
          setSubmitted(null);
        },
      },
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle data-testid="exclude-remaining-title" className="text-lg font-semibold">
          {`Exclude ${remaining.toLocaleString()} product${plural} from this batch?`}
        </DialogTitle>
        <DialogDescription className="text-muted-foreground text-sm">
          They stop counting as issues, the batch can complete, and they won&apos;t be delivered
          with this batch. They stay in your catalog unenriched, so the next run picks them up
          again. You can undo this until the batch is delivered.
        </DialogDescription>
      </DialogHeader>

      <div className="bg-muted/40 rounded-lg border px-4 py-3">
        <p className="text-muted-foreground mb-2 text-xs">Why they were not enriched</p>
        <ReasonBars bars={bars} />
      </div>

      <fieldset className="space-y-2">
        <legend className="mb-1 text-sm font-medium">Reason</legend>
        <RadioGroup
          value={reason ?? ''}
          onValueChange={(v) => {
            setReason(v as BatchExclusionReason);
          }}
          className="gap-2"
          data-testid="exclude-remaining-reason-group"
        >
          {REASON_OPTIONS.map((o) => (
            <label
              key={o.value}
              htmlFor={`exclude-reason-${o.value}`}
              className="hover:border-primary/50 has-[:checked]:border-primary has-[:checked]:bg-primary/5 flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 text-sm"
            >
              <RadioGroupItem
                id={`exclude-reason-${o.value}`}
                data-testid={`exclude-remaining-reason-${o.value}`}
                value={o.value}
                className="size-4"
              />
              <span>{o.label}</span>
            </label>
          ))}
        </RadioGroup>
      </fieldset>

      <div className="space-y-1">
        <label htmlFor="exclude-remaining-note" className="text-sm font-medium">
          Note <span className="text-muted-foreground font-normal">(optional)</span>
        </label>
        <Textarea
          id="exclude-remaining-note"
          data-testid="exclude-remaining-note"
          value={note}
          maxLength={EXCLUDE_NOTE_MAX_LENGTH}
          rows={2}
          placeholder="e.g. 503s all week"
          onChange={(e) => {
            setNote(e.target.value);
          }}
        />
        <p className="text-muted-foreground text-right text-xs tabular-nums">
          {note.length}/{EXCLUDE_NOTE_MAX_LENGTH}
        </p>
      </div>

      <DialogFooter className="gap-2">
        <Button
          data-testid="exclude-remaining-cancel"
          variant="outline"
          className="cursor-pointer"
          disabled={isPending}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          data-testid="exclude-remaining-confirm"
          className="cursor-pointer disabled:cursor-not-allowed"
          isLoading={isPending}
          disabled={isPending || !reason || remaining === 0}
          onClick={handleExclude}
        >
          {`Exclude ${remaining.toLocaleString()}`}
        </Button>
      </DialogFooter>
    </>
  );
}
